/**
 * Table Form Modal Component
 * Modal form for creating or editing a table
 */

import React, { useEffect, useState } from 'react';
import { OutletSection, OutletTable } from '../../../types/section';
import { Input, Modal, Select } from '../../common';
import { Button } from '../../common/Button';

interface TableFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, sectionId: number, isHidden: boolean) => void;
  sections: OutletSection[];
  table?: OutletTable | null;
  isLoading?: boolean;
}

export const TableFormModal: React.FC<TableFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  sections,
  table,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (table) {
      setName(table.name);
      setSectionId(String(table.section_id));
      setIsHidden(table.is_hidden);
    } else {
      setName('');
      setSectionId(sections.length > 0 ? String(sections[0].id) : '');
      setIsHidden(false);
    }
  }, [table, isOpen, sections]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !sectionId) return;
    onSubmit(name.trim(), Number(sectionId), isHidden);
  };

  const sectionOptions = sections.map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={table ? 'Edit Table' : 'Add Table'}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Table Name"
          value={name}
          onChange={(val) => setName(String(val))}
          placeholder="e.g. Table 1, VIP Booth"
          required
        />

        <Select
          label="Section"
          options={sectionOptions}
          value={sectionId}
          onChange={(val) => setSectionId(val)}
          placeholder="Select a section"
        />

        <label
          htmlFor="table-hidden-toggle"
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="relative">
            <input
              id="table-hidden-toggle"
              type="checkbox"
              checked={isHidden}
              onChange={(e) => setIsHidden(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-red-400 transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform" />
          </div>
          <span className="text-sm text-gray-700">Hidden</span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            isLoading={isLoading}
            disabled={!name.trim() || !sectionId}
          >
            {table ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
